import { useCallback, useRef } from 'react';

interface SearchResult {
  url: string;
  title: string;
  date?: string;
}

interface PerplexityStreamHandlerReturn {
  handlePerplexityStreamUpdate: (
    value: Uint8Array,
    decoder: TextDecoder,
    setResponseToShow: (content: string) => void,
    setExploredLinks: (
      links: { link: string; title: string; date?: string }[],
    ) => void,
    accumulatedContent: React.MutableRefObject<string>,
    searchResults: React.MutableRefObject<SearchResult[]>,
  ) => void;
  resetBuffer: () => void;
}

export function usePerplexityStreamHandler(): PerplexityStreamHandlerReturn {
  const accumulatedBuffer = useRef<string>('');

  const handlePerplexityStreamUpdate = useCallback(
    (
      value: Uint8Array,
      decoder: TextDecoder,
      setResponseToShow: (content: string) => void,
      setExploredLinks: (
        links: { link: string; title: string; date?: string }[],
      ) => void,
      accumulatedContent: React.MutableRefObject<string>,
      searchResults: React.MutableRefObject<SearchResult[]>,
    ) => {
      const chunk = decoder.decode(value);

      // Accumulate raw chunk data to handle split JSON objects
      accumulatedBuffer.current += chunk;
      // Parse SSE chunks - look for complete "data: {json}\n" messages
      const lines = accumulatedBuffer.current.split('\n');
      // Keep the last incomplete line in the buffer (might be partial JSON)
      accumulatedBuffer.current = lines.pop() || '';

      // Process complete lines only
      for (const line of lines) {
        if (line.startsWith('data: ') && line !== 'data: [DONE]') {
          try {
            const jsonStr = line.substring(6); // Remove "data: " prefix
            const data = JSON.parse(jsonStr);

            // Extract search results and citations when available
            if (data.search_results) {
              searchResults.current = data.search_results;
              setExploredLinks(
                searchResults.current.map((link) => ({
                  link: link.url,
                  title: link.title,
                  date: link.date,
                })),
              );
            }

            // Extract content from the message
            if (data.choices?.[0]?.message?.content) {
              const content = data.choices[0].message.content;
              accumulatedContent.current = content;
              setResponseToShow(accumulatedContent.current);
            }
          } catch (parseError) {
            console.warn('Failed to parse Perplexity SSE chunk:', parseError);
          }
        }
      }
    },
    [],
  );

  // Reset buffer method for when starting new streams
  const resetBuffer = useCallback(() => {
    accumulatedBuffer.current = '';
  }, []);

  return { handlePerplexityStreamUpdate, resetBuffer };
}
